import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { EnterpriseAdminService } from '../../enterprise-admin/enterprise-admin.service';

type AgentLibraryType = 'voice' | 'whatsapp';

@Component({
  selector: 'hour4u-admin-create-agent',
  templateUrl: './create-agent.component.html',
  styleUrls: ['./create-agent.component.scss']
})
export class CreateAgentComponent implements OnInit {
  @ViewChild('stepper', { static: false }) stepper: MatStepper;

  profile = this.appService.getCurrentAccount();
  isLoading = false;
  isSaving = false;
  isGenerating = false;
  isEdit = false;
  agentId: string = null;
  diameter = 40;
  loaderColor: string;
  mode = 'indeterminate';

  typeForm: FormGroup;
  ideaForm: FormGroup;
  configForm: FormGroup;

  agentTypes: Array<{ value: AgentLibraryType; label: string; description: string }> = [
    {
      value: 'voice',
      label: 'Voice',
      description: 'Outbound by default. Optionally use for incoming calls.'
    },
    {
      value: 'whatsapp',
      label: 'WhatsApp',
      description: 'Chat agent with prompt and optional WA number for project bind.'
    }
  ];

  voices: any[] = [];
  numbers: any[] = [];
  models: any[] = [];
  languages: any[] = [];
  ambientAudios: any[] = [];
  emotions: any[] = [];
  voiceAgents: any[] = [];
  promptVariables: string[] = [];
  promptPlaceholder = 'Write or paste the agent prompt. Use variables like {{name}}.';

  private readonly promptVarRe = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private enterpriseAdminService: EnterpriseAdminService,
    private notify: NotificationService
  ) {}

  ngOnInit() {
    this.typeForm = this.fb.group({
      agentType: ['voice', Validators.required]
    });

    this.ideaForm = this.fb.group({
      agentName: ['', Validators.required],
      persona: [''],
      idea: [''],
      prompt: ['', Validators.required],
      welcomeMessage: [''],
      sourceAgentId: ['']
    });

    this.configForm = this.fb.group({
      voiceId: [''],
      callTokenId: [''],
      numberId: [''],
      modelId: [''],
      language: ['Hinglish'],
      transcriptionProvider: [''],
      ambientAudio: [''],
      emotionId: [''],
      useAsInbound: [false],
      callEndDuration: [25000],
      initialDisableMicDuration: [3000],
      maxCallDuration: [600],
      maxDurationGoodbyeMessage: [
        "It's been great speaking with you. I'll let you get back to your day."
      ],
      callUserAlert: [false],
      callUserAlertDuration: [15000],
      silenceAlertMessage: ['Are you still there?'],
      callEndConditions: this.fb.array([
        this.buildEndCondition(true, 'user asks to stop calling'),
        this.buildEndCondition(false, 'in all other situations')
      ]),
      whatsappPhoneNumber: [''],
      whatsappVendor: ['serri'],
      whatsappVendorProjectId: ['']
    });

    this.ideaForm.get('prompt').valueChanges.subscribe(() => this.refreshPromptVariables());
    this.ideaForm.get('welcomeMessage').valueChanges.subscribe(() => this.refreshPromptVariables());
    this.ideaForm.get('agentName').valueChanges.subscribe(() => {
      if (!this.isEdit) {
        const current = String(this.ideaForm.get('welcomeMessage').value || '').trim();
        // Refresh default greeting while user still has the auto template.
        if (!current || current.startsWith('Hi, this is ') || current.startsWith('Hi {{name}}, this is ')) {
          this.ensureDefaultGreeting(true);
        }
      }
    });

    this.agentId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.agentId;
    this.loadCatalogs();
    this.loadVoiceAgents();

    if (this.isEdit) {
      this.loadExistingAgent();
    } else {
      this.ensureDefaultGreeting(true);
      this.refreshPromptVariables();
    }
  }

  get selectedType(): AgentLibraryType {
    return this.typeForm.get('agentType').value;
  }

  get isVoice(): boolean {
    return this.selectedType === 'voice';
  }

  get agentDisplayName(): string {
    return (this.ideaForm && this.ideaForm.get('agentName').value) || 'Voice agent';
  }

  get canGeneratePrompt(): boolean {
    if (!this.ideaForm) {
      return false;
    }
    const name = String(this.ideaForm.get('agentName').value || '').trim();
    const idea = String(this.ideaForm.get('idea').value || '').trim();
    return !!name && !!idea && !this.isGenerating;
  }

  get generatePromptTooltip(): string {
    return this.canGeneratePrompt ? '' : 'Name and idea are required';
  }

  get greetingPlaceholder(): string {
    return this.defaultGreeting();
  }

  get callEndConditions(): FormArray {
    return this.configForm.get('callEndConditions') as FormArray;
  }

  buildEndCondition(cut: boolean, value: string): FormGroup {
    return this.fb.group({
      cut: [cut],
      value: [value || '']
    });
  }

  addEndCondition() {
    this.callEndConditions.push(this.buildEndCondition(true, ''));
  }

  removeEndCondition(index: number) {
    if (this.callEndConditions.length <= 1) {
      return;
    }
    this.callEndConditions.removeAt(index);
  }

  selectType(type: AgentLibraryType) {
    this.typeForm.patchValue({ agentType: type });
    if (type !== 'voice') {
      this.configForm.patchValue({ useAsInbound: false });
    }
    this.ensureDefaultGreeting();
  }

  private defaultGreeting(): string {
    const name = String((this.ideaForm && this.ideaForm.get('agentName').value) || '').trim()
      || 'your assistant';
    if (this.selectedType === 'whatsapp') {
      return `Hi {{name}}, this is ${name}. How can I help?`;
    }
    return `Hi, this is ${name}. Is this a good time to talk?`;
  }

  ensureDefaultGreeting(force = false) {
    if (!this.ideaForm) {
      return;
    }
    const current = String(this.ideaForm.get('welcomeMessage').value || '').trim();
    if (force || !current) {
      this.ideaForm.patchValue({ welcomeMessage: this.defaultGreeting() }, { emitEvent: true });
    }
  }

  refreshPromptVariables() {
    const texts = [
      (this.ideaForm && this.ideaForm.get('prompt').value) || '',
      (this.ideaForm && this.ideaForm.get('welcomeMessage').value) || ''
    ];
    const seen: { [k: string]: boolean } = {};
    const vars: string[] = [];
    texts.forEach(text => {
      this.promptVarRe.lastIndex = 0;
      let match = this.promptVarRe.exec(text);
      while (match) {
        const name = match[1];
        if (name && !seen[name]) {
          seen[name] = true;
          vars.push(name);
        }
        match = this.promptVarRe.exec(text);
      }
    });
    this.promptVariables = vars;
  }

  loadCatalogs() {
    this.enterpriseAdminService.getVoices().subscribe(
      res => { this.voices = (res && res.items) || []; },
      () => { this.voices = []; }
    );
    this.enterpriseAdminService.getNumbers().subscribe(
      res => { this.numbers = (res && res.items) || []; },
      () => { this.numbers = []; }
    );
    this.enterpriseAdminService.getModels().subscribe(
      res => { this.models = (res && res.items) || []; },
      () => { this.models = []; }
    );
    this.enterpriseAdminService.getLanguages().subscribe(
      res => { this.languages = (res && res.items) || []; },
      () => { this.languages = []; }
    );
    this.enterpriseAdminService.getAmbientAudios().subscribe(
      res => { this.ambientAudios = (res && res.items) || []; },
      () => { this.ambientAudios = []; }
    );
    this.enterpriseAdminService.getEmotions().subscribe(
      res => { this.emotions = (res && res.items) || []; },
      () => { this.emotions = []; }
    );
  }

  loadVoiceAgents() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    if (!entityId) {
      return;
    }
    this.enterpriseAdminService.getAllAgentsByEntityId(entityId, 200, 1).subscribe(
      res => {
        const items = (res && res.items) || [];
        this.voiceAgents = items.filter(a => {
          const t = (a.agent_type || '').toLowerCase();
          return t.includes('voice') || t === 'outbound' || t === 'inbound';
        });
      },
      () => { this.voiceAgents = []; }
    );
  }

  loadExistingAgent() {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    if (!entityId || !this.agentId) {
      return;
    }
    this.isLoading = true;
    this.enterpriseAdminService.getAgentById(entityId, this.agentId).subscribe(
      res => {
        const agent = (res && (res.item || res.agent || res)) || {};
        const agentType = this.normalizeType(agent.agent_type || agent.agentType || 'voice');
        const useAsInbound = !!agent.use_as_inbound
          || (agent.agent_type || '').toLowerCase().includes('inbound');
        this.typeForm.patchValue({ agentType });
        this.ideaForm.patchValue({
          agentName: agent.agent_name || agent.agentName || '',
          idea: agent.idea || '',
          prompt: agent.prompt || '',
          welcomeMessage: agent.welcome_message || agent.welcomeMessage || '',
          sourceAgentId: agent.source_agent_id || ''
        });

        while (this.callEndConditions.length) {
          this.callEndConditions.removeAt(0);
        }
        const conditions = Array.isArray(agent.call_end_conditions)
          ? agent.call_end_conditions
          : [];
        if (conditions.length) {
          conditions.forEach((c: any) => {
            this.callEndConditions.push(
              this.buildEndCondition(!!(c && c.cut), (c && c.value) || '')
            );
          });
        } else {
          this.callEndConditions.push(this.buildEndCondition(true, 'user asks to stop calling'));
          this.callEndConditions.push(this.buildEndCondition(false, 'in all other situations'));
        }

        this.configForm.patchValue({
          voiceId: agent.voice_id || '',
          callTokenId: agent.call_token_id || '',
          numberId: agent.number_id || '',
          modelId: agent.model_id || '',
          language: agent.language || 'Hinglish',
          transcriptionProvider: agent.transcription_provider || '',
          ambientAudio: agent.ambient_audio || '',
          emotionId: agent.emotion_id || '',
          useAsInbound,
          callEndDuration: agent.call_end_duration != null ? Number(agent.call_end_duration) : 25000,
          initialDisableMicDuration: agent.initial_disable_mic_duration != null
            ? Number(agent.initial_disable_mic_duration) : 3000,
          maxCallDuration: agent.max_call_duration != null ? Number(agent.max_call_duration) : 600,
          maxDurationGoodbyeMessage: agent.max_duration_goodbye_message
            || "It's been great speaking with you. I'll let you get back to your day.",
          callUserAlert: agent.call_user_alert === true || agent.call_user_alert === 'true',
          callUserAlertDuration: agent.call_user_alert_duration != null
            ? Number(agent.call_user_alert_duration) : 15000,
          silenceAlertMessage: agent.silence_alert_message || 'Are you still there?',
          whatsappPhoneNumber: agent.whatsapp_phone_number || '',
          whatsappVendor: agent.whatsapp_vendor || 'serri',
          whatsappVendorProjectId: agent.whatsapp_vendor_project_id || ''
        });
        this.refreshPromptVariables();
        this.isLoading = false;
      },
      err => {
        this.isLoading = false;
        console.error(err);
        this.notify.showMessage('Failed to load agent', 2500);
      }
    );
  }

  normalizeType(raw: string): AgentLibraryType {
    const t = (raw || '').toLowerCase().replace(/-/g, '_');
    if (t.includes('whatsapp') || t === 'wa') {
      return 'whatsapp';
    }
    return 'voice';
  }

  catalogLabel(item: any): string {
    if (!item) {
      return '';
    }
    return item.name || item.alias || item.label || item.emotion || item.comment
      || item.voice_name || item.model_name || item.language || item.did
      || item.phone_number || item.number || item.value || item.id
      || item.agent_id || JSON.stringify(item);
  }

  catalogValue(item: any, prefer: string[] = ['id', 'voice_id', 'call_token_id', 'model_id', 'number_id']): any {
    if (!item) {
      return '';
    }
    for (const key of prefer) {
      if (item[key] != null && item[key] !== '') {
        return item[key];
      }
    }
    return item.id || item.value || item.emotion_id || '';
  }

  voiceAgentId(agent: any): string {
    return String((agent && (agent.agent_id || agent.agentId || agent.id)) || '');
  }

  private findVoiceAgent(sourceId: string): any {
    const id = String(sourceId || '').trim();
    if (!id) {
      return null;
    }
    return this.voiceAgents.find(a => this.voiceAgentId(a) === id) || null;
  }

  numberCallToken(item: any): string {
    return (item && (item.call_token_id || item.callTokenId || item.id)) || '';
  }

  numberInboundId(item: any): string {
    return (item && (item.id || item.number_id || item.numberId)) || '';
  }

  onOutboundNumberSelected(tokenId: string) {
    const selected = this.numbers.find(
      n => this.numberCallToken(n) === tokenId || this.numberInboundId(n) === tokenId
    );
    this.configForm.patchValue({
      callTokenId: selected ? this.numberCallToken(selected) : (tokenId || '')
    });
  }

  onInboundNumberSelected(numberId: string) {
    this.configForm.patchValue({ numberId: numberId || '' });
  }

  onInboundToggle() {
    if (!this.configForm.get('useAsInbound').value) {
      this.configForm.patchValue({ numberId: '' });
    }
  }

  generatePrompt() {
    if (!this.ideaForm.get('idea').value || !this.ideaForm.get('agentName').value) {
      this.notify.showMessage('Name and idea are required to generate a prompt', 2500);
      return;
    }
    this.isGenerating = true;
    this.enterpriseAdminService.generateAgentPrompt({
      idea: this.ideaForm.get('idea').value,
      agentName: this.ideaForm.get('agentName').value,
      persona: this.ideaForm.get('persona').value,
      language: this.configForm.get('language').value || 'Hinglish',
      agentType: this.selectedType === 'whatsapp'
        ? 'whatsapp'
        : (this.configForm.get('useAsInbound').value ? 'voice_inbound' : 'voice_outbound')
    }).subscribe(
      res => {
        this.isGenerating = false;
        if (res && res.prompt) {
          this.ideaForm.patchValue({
            prompt: res.prompt,
            welcomeMessage: res.welcome_message || this.ideaForm.get('welcomeMessage').value
          });
          this.refreshPromptVariables();
          this.notify.showMessage('Prompt generated', 2000);
        } else {
          this.notify.showMessage((res && res.error) || 'Prompt generation failed', 2500);
        }
      },
      err => {
        this.isGenerating = false;
        console.error(err);
        this.notify.showMessage('Prompt generation failed', 2500);
      }
    );
  }

  onSourceVoiceSelected() {
    const sourceId = this.ideaForm.get('sourceAgentId').value;
    const src = this.findVoiceAgent(sourceId);
    if (!src) {
      return;
    }
    // Keep selection only; user chooses Copy or Create WhatsApp prompt.
    if (!String(this.ideaForm.get('agentName').value || '').trim()) {
      this.ideaForm.patchValue({
        agentName: `${src.agent_name || src.agentName || 'Agent'} WhatsApp`
      });
    }
    this.notify.showMessage('Voice agent selected — copy or create a WhatsApp prompt', 2500);
  }

  copyPromptFromVoiceAgent() {
    const src = this.findVoiceAgent(this.ideaForm.get('sourceAgentId').value);
    if (!src) {
      this.notify.showMessage('Select a voice agent first', 2000);
      return;
    }
    const greeting = src.welcome_message || src.welcomeMessage || src.initial_greeting || '';
    this.ideaForm.patchValue({
      prompt: src.prompt || '',
      welcomeMessage: greeting || this.defaultGreeting(),
      idea: src.idea || this.ideaForm.get('idea').value || '',
      agentName: this.ideaForm.get('agentName').value
        || `${src.agent_name || src.agentName || 'Agent'} WhatsApp`
    });
    this.refreshPromptVariables();
    this.notify.showMessage('Prompt and greeting copied from voice agent', 2000);
  }

  generatePromptFromVoiceAgent() {
    const src = this.findVoiceAgent(this.ideaForm.get('sourceAgentId').value);
    if (!src) {
      this.notify.showMessage('Select a voice agent first', 2000);
      return;
    }
    const sourcePrompt = String(src.prompt || '').trim();
    if (!sourcePrompt) {
      this.notify.showMessage('Selected voice agent has no prompt to adapt', 2500);
      return;
    }
    const agentName = String(this.ideaForm.get('agentName').value || '').trim()
      || `${src.agent_name || src.agentName || 'Agent'} WhatsApp`;
    this.ideaForm.patchValue({
      agentName,
      idea: sourcePrompt,
      welcomeMessage: src.welcome_message || src.welcomeMessage || src.initial_greeting
        || this.defaultGreeting()
    });
    this.isGenerating = true;
    this.enterpriseAdminService.generateAgentPrompt({
      idea: sourcePrompt,
      agentName,
      persona: this.ideaForm.get('persona').value || 'WhatsApp chat, concise and helpful',
      language: this.configForm.get('language').value || 'Hinglish',
      agentType: 'whatsapp'
    }).subscribe(
      res => {
        this.isGenerating = false;
        if (res && res.prompt) {
          this.ideaForm.patchValue({
            prompt: res.prompt,
            welcomeMessage: res.welcome_message
              || this.ideaForm.get('welcomeMessage').value
              || this.defaultGreeting()
          });
          this.refreshPromptVariables();
          this.notify.showMessage('WhatsApp prompt created from voice agent', 2000);
        } else {
          this.notify.showMessage((res && res.error) || 'Prompt generation failed', 2500);
        }
      },
      err => {
        this.isGenerating = false;
        console.error(err);
        this.notify.showMessage('Prompt generation failed', 2500);
      }
    );
  }

  cancel() {
    this.router.navigate(['/agents']);
  }

  private persistedAgentType(): string {
    if (this.selectedType === 'whatsapp') {
      return 'whatsapp';
    }
    return this.configForm.get('useAsInbound').value ? 'voice_inbound' : 'voice_outbound';
  }

  save() {
    if (!this.ideaForm.get('agentName').value || !this.ideaForm.get('prompt').value) {
      this.ideaForm.markAllAsTouched();
      this.notify.showMessage('Please complete name and prompt', 2500);
      return;
    }
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const userId = this.profile && this.profile.user && this.profile.user.id;
    if (!entityId) {
      this.notify.showMessage('Entity missing', 2500);
      return;
    }

    this.refreshPromptVariables();
    const callEndConditions = (this.callEndConditions.value || [])
      .filter((c: any) => c && String(c.value || '').trim())
      .map((c: any) => ({ cut: !!c.cut, value: String(c.value).trim() }));

    const body: any = {
      entityId,
      userId,
      agentName: this.ideaForm.get('agentName').value,
      agentType: this.persistedAgentType(),
      idea: this.ideaForm.get('idea').value,
      prompt: this.ideaForm.get('prompt').value,
      welcomeMessage: this.ideaForm.get('welcomeMessage').value,
      language: this.configForm.get('language').value || 'Hinglish',
      voiceId: this.configForm.get('voiceId').value,
      callTokenId: this.configForm.get('callTokenId').value || '',
      numberId: this.configForm.get('useAsInbound').value
        ? (this.configForm.get('numberId').value || '')
        : '',
      modelId: this.configForm.get('modelId').value,
      transcriptionProvider: this.configForm.get('transcriptionProvider').value,
      ambientAudio: this.configForm.get('ambientAudio').value,
      emotionId: this.configForm.get('emotionId').value,
      useAsInbound: this.selectedType === 'voice' && !!this.configForm.get('useAsInbound').value,
      inputsNeeded: this.promptVariables.slice(),
      callEndDuration: Number(this.configForm.get('callEndDuration').value) || 0,
      initialDisableMicDuration: Number(this.configForm.get('initialDisableMicDuration').value) || 0,
      maxCallDuration: Number(this.configForm.get('maxCallDuration').value) || 0,
      maxDurationGoodbyeMessage: this.configForm.get('maxDurationGoodbyeMessage').value || '',
      callUserAlert: !!this.configForm.get('callUserAlert').value,
      callUserAlertDuration: Number(this.configForm.get('callUserAlertDuration').value) || 0,
      silenceAlertMessage: this.configForm.get('silenceAlertMessage').value || '',
      callEndConditions,
      whatsappPhoneNumber: this.configForm.get('whatsappPhoneNumber').value,
      whatsappVendor: this.configForm.get('whatsappVendor').value,
      whatsappVendorProjectId: this.configForm.get('whatsappVendorProjectId').value,
      sourceAgentId: this.ideaForm.get('sourceAgentId').value || undefined
    };

    if (this.isEdit && this.agentId) {
      body.agentId = this.agentId;
    }

    this.isSaving = true;
    const req = this.isEdit
      ? this.enterpriseAdminService.updateLibraryAgent(body)
      : this.enterpriseAdminService.createLibraryAgent(body);

    req.subscribe(
      res => {
        this.isSaving = false;
        if (res && (res.success || res.agent)) {
          const providerUpdate = res.provider && res.provider.update;
          if (providerUpdate && providerUpdate.ok === false) {
            const detail =
              (providerUpdate.data && (providerUpdate.data.message || providerUpdate.data.error)) ||
              providerUpdate.error ||
              'Catalog sync failed';
            this.notify.showMessage(`Saved locally, but voice sync failed: ${detail}`, 4500);
          } else {
            this.notify.showMessage(this.isEdit ? 'Agent updated' : 'Agent created', 2000);
          }
          this.router.navigate(['/agents']);
        } else {
          this.notify.showMessage((res && res.error) || 'Save failed', 3000);
        }
      },
      err => {
        this.isSaving = false;
        console.error(err);
        const msg = (err && err.error && err.error.error) || 'Save failed';
        this.notify.showMessage(msg, 3000);
      }
    );
  }
}
